// @ts-check
// Menu "Opções" da barra de view (doc 06, seção 4): no kanban, campos do cartão e a ordem deles, modo compacto,
// ocultar colunas vazias e colunas ocultas; na tabela, agrupar por campo de seleção, ocultar grupos vazios e grupos
// ocultos. Cada escolha é gravada na view na hora, como no Twenty.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { fieldsById, normalizeView, replaceViewLocally } from '../../data/metadata-store.js';
import { updateView } from '../../data/rest.js';
import { FIELD_TYPE, getFieldIcon, getLabelIdentifierField, isFieldDisplayable, findOption } from '../../lib/field-metadata.js';
import { Icon } from '../../ui/icon.js';
import { LightIconButton } from '../../ui/button.js';
import { Toggle } from '../../ui/feedback.js';
import { Dropdown, DropdownMenuHeader, DropdownMenuItems, DropdownMenuSectionLabel, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { buildViewShareUrl } from './view-state.js';
import { VIEW_TYPE_ICONS, copyViewLink, getCalendarFields, getGroupableFields } from './view-picker.js';
import {
  applyViewFieldOrder,
  getCardFieldLists,
  getRestorableHiddenGroups,
  moveIdBy,
  setGroupVisibility,
  setViewFieldVisibility,
} from '../object-record/view-layout.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../../data/metadata-store.js').View} View
 */

/**
 * Grava uma mudança da view: aplica na tela, manda para a API e volta atrás se a API recusar. A resposta da API
 * substitui a cópia local (o motor cria os grupos quando o campo de agrupamento muda).
 * @param {View} view @param {Record<string, unknown>} patch
 */
export async function persistViewPatch(view, patch) {
  replaceViewLocally(/** @type {View} */ ({ ...view, ...patch }));
  try {
    const saved = await updateView(view.id, patch);
    if (saved && typeof saved === 'object' && saved.id === view.id) replaceViewLocally(normalizeView(saved));
  } catch (error) {
    replaceViewLocally(view);
    showErrorSnackbar(error);
  }
}

/** Campo de agrupamento da tabela: só SELECT, como pede a paridade (relações agrupam no kanban). @param {View} view */
export function getTableGroupField(view) {
  if (view.type !== 'TABLE' || !view.mainGroupByFieldMetadataId) return undefined;
  const field = fieldsById.value.get(view.mainGroupByFieldMetadataId);
  return field?.type === FIELD_TYPE.SELECT ? field : undefined;
}

/** @param {ObjectMetadata} objectMetadata */
const getTableGroupableFields = (objectMetadata) => objectMetadata.fields.filter((field) => field.type === FIELD_TYPE.SELECT && field.isActive !== false && !field.isSystem);

/**
 * Campos que podem aparecer no cartão do kanban: os exibíveis, menos o título e o campo das colunas.
 * @param {ObjectMetadata} objectMetadata @param {View} view
 */
export function getBoardCardFieldLists(objectMetadata, view) {
  const labelField = getLabelIdentifierField(objectMetadata);
  const excluded = new Set([labelField?.id, view.mainGroupByFieldMetadataId].filter(Boolean).map(String));
  const candidates = objectMetadata.fields.filter((field) => isFieldDisplayable(field) && field.type !== FIELD_TYPE.RICH_TEXT && !(field.isSystem && field.name !== 'createdAt' && field.name !== 'updatedAt'));
  return getCardFieldLists(view.viewFields, candidates, excluded);
}

/**
 * Item de menu com chave liga/desliga à direita.
 * @param {{ icon: string, text: string, value: boolean, onChange: (value: boolean) => void, testId?: string }} props
 */
function ToggleMenuItem({ icon, text, value, onChange, testId }) {
  return html`<div data-testid=${testId}>
    <${MenuItem} icon=${icon} text=${text} onClick=${() => onChange(!value)}
      rightComponent=${html`<${Toggle} value=${value} size="small" ariaLabel=${text} onChange=${onChange} />`} />
  </div>`;
}

/**
 * @param {{ objectMetadata: ObjectMetadata, view: View }} props
 */
export function ViewOptionsButton({ objectMetadata, view }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState(/** @type {'root' | 'cardFields' | 'groupBy' | 'hiddenGroups' | 'layout'} */ ('root'));
  if (view.type !== 'KANBAN' && view.type !== 'TABLE' && view.type !== 'CALENDAR') return null;
  const isBoard = view.type === 'KANBAN';
  const isCalendar = view.type === 'CALENDAR';
  const groupField = isBoard
    ? (view.mainGroupByFieldMetadataId ? fieldsById.value.get(view.mainGroupByFieldMetadataId) : undefined)
    : getTableGroupField(view);
  const hiddenGroups = groupField?.type === FIELD_TYPE.SELECT ? getRestorableHiddenGroups(view.viewGroups, groupField) : [];
  const close = () => {
    setIsOpen(false);
    setMode('root');
  };
  const groupLabel = (/** @type {string | null} */ value) => {
    if (value === null) return t('board.noValue');
    const option = groupField ? findOption(groupField, value) : undefined;
    return option ? translateMetadataLabel(option.label) : value;
  };

  const isIndexView = view.key === 'INDEX';
  const isFallbackView = view.id.startsWith('fallback-');
  // itens comuns aos três tipos (doc 06, seção 4.2): trocar o tipo da view e copiar o link com os filtros atuais
  const commonItems = html`
    ${!isIndexView && !isFallbackView
      ? html`<${MenuItem} icon=${VIEW_TYPE_ICONS[view.type] ?? 'IconTable'} text=${t('viewsFilters.options.layout')} hasSubMenu=${true}
          contextualText=${t(`viewTypes.${view.type}`)} onClick=${() => setMode('layout')} />`
      : null}
    <${MenuItem} icon="IconLink" text=${t('viewsFilters.views.copyLink')} onClick=${async () => {
      try {
        await copyViewLink(buildViewShareUrl(view, objectMetadata.namePlural));
      } catch (error) {
        showErrorSnackbar(error);
      }
      close();
    }} />`;

  /** @type {any} */
  let content;
  if (mode === 'layout') {
    const groupableFields = getGroupableFields(objectMetadata);
    const calendarFields = getCalendarFields(objectMetadata);
    /** @param {string} type */
    const changeType = (type) => {
      close();
      if (type === view.type) return;
      /** @type {Record<string, unknown>} */
      const patch = { type, icon: view.icon === VIEW_TYPE_ICONS[view.type] ? VIEW_TYPE_ICONS[type] : view.icon };
      // kanban precisa de campo das colunas e calendário de campo de data: o primeiro disponível, como no Twenty
      if (type === 'KANBAN' && !view.mainGroupByFieldMetadataId) patch.mainGroupByFieldMetadataId = groupableFields[0]?.id ?? null;
      if (type === 'CALENDAR' && !view.calendarFieldMetadataId) patch.calendarFieldMetadataId = calendarFields[0]?.id ?? null;
      persistViewPatch(view, patch);
    };
    content = html`
      <${DropdownMenuHeader} title=${t('viewsFilters.options.layout')} onBack=${() => setMode('root')} />
      <${DropdownMenuItems}>
        <${MenuItem} icon="IconTable" text=${t('viewTypes.TABLE')} selected=${view.type === 'TABLE'} onClick=${() => changeType('TABLE')} />
        <${MenuItem} icon="IconLayoutKanban" text=${t('viewTypes.KANBAN')} selected=${isBoard} disabled=${groupableFields.length === 0}
          contextualText=${groupableFields.length === 0 ? t('viewsFilters.options.noGroupField') : undefined} onClick=${() => changeType('KANBAN')} />
        <${MenuItem} icon="IconCalendar" text=${t('viewTypes.CALENDAR')} selected=${isCalendar} disabled=${calendarFields.length === 0}
          contextualText=${calendarFields.length === 0 ? t('viewsFilters.options.noDateField') : undefined} onClick=${() => changeType('CALENDAR')} />
      <//>`;
  } else if (isCalendar) {
    content = html`<${DropdownMenuItems}>${commonItems}<//>`;
  } else if (mode === 'cardFields') {
    const { visible, hidden } = getBoardCardFieldLists(objectMetadata, view);
    const visibleIds = visible.map((field) => field.id);
    const reorder = (/** @type {string} */ fieldId, /** @type {number} */ offset) => {
      const labelField = getLabelIdentifierField(objectMetadata);
      const ordered = [...(labelField ? [labelField.id] : []), ...moveIdBy(visibleIds, fieldId, offset)];
      persistViewPatch(view, { viewFields: applyViewFieldOrder(view.viewFields, ordered) });
    };
    content = html`
      <${DropdownMenuHeader} title=${t('boardTable.options.cardFields')} onBack=${() => setMode('root')} />
      <${DropdownMenuItems} hasMaxHeight=${true}>
        <${DropdownMenuSectionLabel} label=${t('boardTable.options.visibleFields')} />
        ${visible.length === 0 ? html`<div class="menu-item-empty">${t('boardTable.options.noCardFields')}</div>` : null}
        ${visible.map((field, index) => html`<${MenuItem}
          key=${field.id}
          icon=${getFieldIcon(field)}
          text=${translateMetadataLabel(field.label)}
          rightComponent=${html`<span class="view-options-field-actions">
            <${LightIconButton} icon="IconArrowUp" accent="tertiary" ariaLabel=${t('boardTable.options.moveUp')} disabled=${index === 0} onClick=${(/** @type {Event} */ event) => {
              event.stopPropagation();
              reorder(field.id, -1);
            }} />
            <${LightIconButton} icon="IconArrowDown" accent="tertiary" ariaLabel=${t('boardTable.options.moveDown')} disabled=${index === visible.length - 1} onClick=${(/** @type {Event} */ event) => {
              event.stopPropagation();
              reorder(field.id, 1);
            }} />
            <${LightIconButton} icon="IconEyeOff" accent="tertiary" ariaLabel=${t('boardTable.options.hideField')} onClick=${(/** @type {Event} */ event) => {
              event.stopPropagation();
              persistViewPatch(view, { viewFields: setViewFieldVisibility(view.viewFields, field.id, false) });
            }} />
          </span>`}
        />`)}
        ${hidden.length > 0
          ? html`<${DropdownMenuSeparator} />
            <${DropdownMenuSectionLabel} label=${t('boardTable.options.hiddenFields')} />
            ${hidden.map((field) => html`<${MenuItem}
              key=${field.id}
              icon=${getFieldIcon(field)}
              text=${translateMetadataLabel(field.label)}
              rightComponent=${html`<${Icon} name="IconEye" size=${16} />`}
              onClick=${() => persistViewPatch(view, { viewFields: setViewFieldVisibility(view.viewFields, field.id, true) })}
            />`)}`
          : null}
      <//>`;
  } else if (mode === 'groupBy') {
    content = html`
      <${DropdownMenuHeader} title=${t('boardTable.options.groupBy')} onBack=${() => setMode('root')} />
      <${DropdownMenuItems} hasMaxHeight=${true}>
        <${MenuItem} icon="IconCircleOff" text=${t('boardTable.options.noGrouping')} selected=${!groupField} onClick=${() => {
          persistViewPatch(view, { mainGroupByFieldMetadataId: null });
          close();
        }} />
        ${getTableGroupableFields(objectMetadata).map((field) => html`<${MenuItem}
          key=${field.id}
          icon=${getFieldIcon(field)}
          text=${translateMetadataLabel(field.label)}
          selected=${groupField?.id === field.id}
          onClick=${() => {
            if (groupField?.id !== field.id) persistViewPatch(view, { mainGroupByFieldMetadataId: field.id });
            close();
          }}
        />`)}
      <//>`;
  } else if (mode === 'hiddenGroups' && groupField) {
    content = html`
      <${DropdownMenuHeader} title=${t(isBoard ? 'boardTable.options.hiddenColumns' : 'boardTable.options.hiddenGroups')} onBack=${() => setMode('root')} />
      <${DropdownMenuItems} hasMaxHeight=${true}>
        ${hiddenGroups.length === 0 ? html`<div class="menu-item-empty">${t('boardTable.options.noHiddenGroups')}</div>` : null}
        ${hiddenGroups.map((group) => html`<${MenuItem}
          key=${group.key}
          text=${groupLabel(group.value)}
          rightComponent=${html`<${Icon} name="IconEye" size=${16} />`}
          onClick=${() => persistViewPatch(view, { viewGroups: setGroupVisibility(view.viewGroups, groupField, group.key, true) })}
        />`)}
      <//>`;
  } else {
    const cardFieldCount = isBoard ? getBoardCardFieldLists(objectMetadata, view).visible.length : 0;
    content = html`<${DropdownMenuItems}>
      ${commonItems}
      <${DropdownMenuSeparator} />
      ${isBoard
        ? html`
          <${MenuItem} icon="IconListDetails" text=${t('boardTable.options.cardFields')} hasSubMenu=${true}
            contextualText=${t('boardTable.options.shownCount', { count: cardFieldCount })} onClick=${() => setMode('cardFields')} />
          <${ToggleMenuItem} icon="IconBaselineDensitySmall" text=${t('boardTable.options.compact')} value=${Boolean(view.isCompact)} testId="view-option-compact"
            onChange=${(value) => persistViewPatch(view, { isCompact: value })} />`
        : html`<${MenuItem}
            icon="IconLayoutList"
            text=${t('boardTable.options.groupBy')}
            hasSubMenu=${!isIndexView}
            disabled=${isIndexView}
            contextualText=${isIndexView ? t('boardTable.options.groupByIndexHint') : groupField ? translateMetadataLabel(groupField.label) : t('boardTable.options.noGrouping')}
            onClick=${() => setMode('groupBy')}
          />`}
      ${groupField
        ? html`
          <${ToggleMenuItem} icon="IconCircleOff" text=${t(isBoard ? 'boardTable.options.hideEmptyColumns' : 'boardTable.options.hideEmptyGroups')}
            value=${Boolean(view.shouldHideEmptyGroups)} testId="view-option-hide-empty"
            onChange=${(value) => persistViewPatch(view, { shouldHideEmptyGroups: value })} />
          ${hiddenGroups.length > 0
            ? html`<${MenuItem} icon="IconEyeOff" hasSubMenu=${true} contextualText=${String(hiddenGroups.length)}
                text=${t(isBoard ? 'boardTable.options.hiddenColumns' : 'boardTable.options.hiddenGroups')} onClick=${() => setMode('hiddenGroups')} />`
            : null}`
        : null}
    <//>`;
  }

  return html`<span class="view-options">
    <button ref=${anchorRef} type="button" class="header-dropdown-button" data-testid="view-options-button" onClick=${() => (isOpen ? close() : setIsOpen(true))}>
      <span>${t('boardTable.options.title')}</span>
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${close} width=${280} placement="bottom-end">
      ${content}
    <//>
  </span>`;
}
